import { Button as AntdButton, Space } from "antd/es";
import { ButtonProps as AntdButtonProps } from "antd/es/button/button";
import _ from "lodash";
import React, { CSSProperties, PureComponent, ReactNode } from "react";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import withDoubleClickEdit from "../../../shared/hoc/with-double-click-edit";
import { KMainFunctions } from "../../../shared/utilty/main-functions";
import { CommonProps } from "../common-props";
import { Icon } from "../icon/icon";

interface ButtonProps extends AntdButtonProps {
  label?: string;
  icon?: string;
  style?: any;
  iconColor?: string;
  iconPosition?: "left" | "right";
}

interface ButtonState {}

class Button extends PureComponent<ButtonProps & CommonProps, ButtonState> {
  constructor(props: ButtonProps) {
    super(props);
    this.state = {};
  }

  getProps = () => {
    const props: any = _.clone(this.props);
    const style: any = _.clone(this.props.style);
    style.whiteSpace = "normal";

    // With whiteSpace:normal, long labels wrap to multiple lines. antd's fixed
    // .ant-btn height (32/24/40px for middle/small/large) then causes the
    // wrapped lines to render outside the button box and bleed into the
    // section below. Let the button grow vertically while preserving the
    // antd default size as a minimum so short-label buttons look unchanged.
    if (!style.height) {
      const size = this.props.size || "middle";
      const defaultMinHeight = size === "large" ? "40px" : size === "small" ? "24px" : "32px";
      style.minHeight = style.minHeight || defaultMinHeight;
      style.height = "auto";
    }

    if (style.minHeight && style.textAlign) {
      style.display = "inline-flex";
      style.alignItems = style.alignItems || "center";
      style.justifyContent = KMainFunctions.getTextAlignAsJustifyContent(style.textAlign) || "center";
    }

    props.style = style;
    if (props.icon && props.icon.length > 0) {
      delete props.icon;
    }
    if (props.label) {
      delete props.label;
    }
    if (props.iconColor) {
      delete props.iconColor;
    }
    if (props.style.fontFamily) {
      delete props.style.fontFamily;
    }
    if (props.style.fontSize) {
      delete props.style.fontSize;
    }
    if (props.style.fontWeight) {
      delete props.style.fontWeight;
    }
    if (!props.style?.backgroundColor) {
      props.style.backgroundColor = "transparent";
    }
    if (!props.style.borderColor) {
      props.style.borderColor = "transparent";
    }
    props.block = this.props.style?.display === "block";
    if (props.href === "") delete props.href;

    // Remove false boolean props to avoid React warnings
    if (props.block === false) {
      delete props.block;
    }

    return props;
  };

  getIcon = () => {
    return (
      <Icon
        style={{
          fontSize: this.props.style?.fontSize || "16px",
          position: this.props.label !== undefined ? "relative" : undefined,
          color: this.props.style?.color && this.props.style?.color?.length > 0 ? this.props.style?.color : "white",
          paddingBottom: "2px"
        }}
        iconName={this.props.icon as string}
      />
    );
  };

  getLeftIcon = () => {
    if (this.props.iconPosition !== "right" && this.props.icon && this.props.icon.length > 0) {
      return this.getIcon();
    }
    return <></>;
  };

  getRightIcon = () => {
    if (this.props.iconPosition === "right" && this.props.icon && this.props.icon.length > 0) {
      return this.getIcon();
    }
    return <></>;
  };

  getVerticalAlign = () => {
    const alignItems = this.props.style?.alignItems;
    if (alignItems === "flex-start") return "start";
    if (alignItems === "flex-end") return "end";
    return "center";
  };

  getTextStyles = () => {
    const styles: CSSProperties = {};
    if (this.props.style?.fontFamily) {
      styles.fontFamily = this.props.style?.fontFamily;
    }
    if (this.props.style?.fontSize) {
      styles.fontSize = this.props.style?.fontSize;
    }
    if (this.props.style?.fontWeight) {
      styles.fontWeight = this.props.style?.fontWeight;
    }

    if (this.props.style?.textDecoration) {
      styles.textDecoration = this.props.style?.textDecoration;
    }

    return styles;
  };

  render(): ReactNode {
    const leftIcon = this.getLeftIcon();
    const rightIcon = this.getRightIcon();
    return (
      <>
        {React.createElement(
          AntdButton as any,
          this.getProps(),
          React.createElement(
            Space,
            { style: { height: "100%", ...this.getTextStyles() }, align: this.getVerticalAlign() },
            leftIcon,
            this.props.label && this.props.label.length && this.props.label,
            rightIcon
          )
        )}
      </>
    );
  }
}

const button = withDoubleClickEdit(withCommonEvents(Button), "label");
export { button as Button };
